import { motion, useMotionValueEvent, useScroll, useTransform, type MotionValue } from "framer-motion";
import { CaseSensitive, FileUp, Images, MegaphoneOff, type LucideIcon } from "lucide-react";
import { createRef, useCallback, useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import SectionTitle from "./SectionTitle";

type Key = "fonts" | "backgrounds" | "import" | "ads";
type Variant = "a" | "c";
const KEYS: Key[] = ["fonts", "backgrounds", "import", "ads"];
const ICONS: Record<Key, LucideIcon> = { fonts: CaseSensitive, backgrounds: Images, import: FileUp, ads: MegaphoneOff };

// Card i pins this far below the nav, so each earlier card keeps a 14px edge visible above the next one.
const NAV_H = 64; // matches --nav-h in tokens.css
const pinTop = (i: number) => 12 + i * 14;

// Story Stack's brand blues; the tokens only expose the deep end of this ramp, and these cards need all of it.
const B1 = "#4581e9", B2 = "#356bce", B3 = "#204ca5", B4 = "#0b114d";
const C_TONES: CSSProperties[] = [
  { background: `linear-gradient(170deg,${B1} 0%,${B2} 48%,${B3} 100%)` },
  { background: `radial-gradient(90% 55% at 85% 8%,rgba(69,129,233,.95),transparent 62%),linear-gradient(170deg,${B2},${B4})` },
  { background: `linear-gradient(170deg,${B3} 0%,#132a78 55%,${B4} 100%)` },
  { background: `radial-gradient(80% 50% at 15% 0%,rgba(69,129,233,.95),transparent 65%),linear-gradient(170deg,${B2} 0%,${B3} 58%,${B4} 100%)` },
];

// "What changes with Pro": one 9:16 story card per real Pro feature. Cards pin near the top with CSS sticky; as the next
// card slides over, the previous one shrinks and dims (scroll-linked, inner wrapper only). Reduced motion gets a plain list.
// Both designs use this stack (the owner asked for A's section to work like C's); only the skin differs:
// a = Night Studio ink cards, c = Story Stack brand-blue cards.
export default function FeaturesStack({ variant, id = "features", className }: { variant: Variant; id?: string; className?: string }) {
  const { t } = useTranslation();
  const refs = useMemo(() => KEYS.map(() => createRef<HTMLElement>()), []);
  const [active, setActive] = useState(0);
  const titleId = `${id}-title`;

  const onEnter = useCallback((i: number, v: number) => setActive((a) => (v >= 0.6 ? Math.max(a, i) : a >= i ? Math.max(0, i - 1) : a)), []);

  // Anchors land on a pinned card's current position, so jump to where card i pins instead (plain anchors when cards do not pin).
  const jump = (i: number) => (e: MouseEvent) => {
    const el = refs[i].current;
    if (!el?.parentElement || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    e.preventDefault();
    const stackTop = el.parentElement.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: stackTop + i * el.offsetHeight - NAV_H - pinTop(i) + 2, behavior: "smooth" });
  };

  const stories = KEYS.map((k, i) => (
    <Story
      key={k}
      k={k}
      i={i}
      variant={variant}
      id={`${id}-${k}`}
      selfRef={refs[i]}
      nextRef={refs[i + 1]}
      onEnter={onEnter}
      title={t(`ui.landing.features.items.${k}.title`)}
      body={t(`ui.landing.features.items.${k}.body`)}
    />
  ));

  return (
    <section
      id={id}
      aria-labelledby={titleId}
      className={cn("mx-auto w-full max-w-[1200px] scroll-mt-[var(--nav-h)] px-[var(--gutter)] pt-16 md:pt-0", className)}
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-12">
        <div>
          <div className="md:sticky md:top-0 md:flex md:min-h-[100svh] md:flex-col md:justify-center md:gap-9 md:py-12">
            <SectionTitle id={titleId} title={t("ui.landing.features.title")} />
            <ol className="hidden gap-1.5 md:grid">
              {KEYS.map((k, i) => {
                const Icon = ICONS[k];
                const on = active === i;
                return (
                  <li key={k}>
                    <a
                      href={`#${id}-${k}`}
                      onClick={jump(i)}
                      aria-current={on ? "step" : undefined}
                      className={cn(
                        "inline-flex min-h-[52px] origin-right items-center gap-3.5 rounded-btn pe-6 ps-5 font-display text-lg font-bold leading-snug text-ink lg:text-xl",
                        "transition-[transform,opacity] duration-500 ease-spring hover:opacity-100",
                        on ? "scale-[1.05] opacity-100" : "opacity-60",
                        on && "bg-accent-soft",
                      )}
                    >
                      <Icon className={cn("size-6 shrink-0", on && "text-accent-text")} strokeWidth={1.75} aria-hidden />
                      {t(`ui.landing.features.items.${k}.title`)}
                    </a>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        {/* The last card needs no runway (nothing covers it), so the negative bottom margin pulls the next section up to just
            under it: margin = card height minus article height. The column keeps its full height, so the earlier cards stay
            pinned until the last one has landed on them. */}
        <div className="relative flex flex-col items-center gap-4 motion-safe:gap-0 motion-safe:mb-[calc(min(100%,calc((100svh_-_var(--nav-h)_-_82px)*0.5625),400px)*16/9_-_max(100svh,560px))]">
          {stories}
        </div>
      </div>
    </section>
  );
}

function Story({
  k,
  i,
  variant,
  id,
  selfRef,
  nextRef,
  onEnter,
  title,
  body,
}: {
  k: Key;
  i: number;
  variant: Variant;
  id: string;
  selfRef: RefObject<HTMLElement>;
  nextRef?: RefObject<HTMLElement>;
  onEnter: (i: number, v: number) => void;
  title: string;
  body: string;
}) {
  // enter: 0 when this card's top meets the viewport bottom, 1 when it reaches its pin line (offsetTop follows sticky, so it stays 1
  // while pinned).
  const { scrollYProgress: enter } = useScroll({
    target: selfRef,
    offset: ["start end", `start ${NAV_H + pinTop(i)}px`],
    layoutEffect: false,
  });
  const { scrollYProgress: cover } = useScroll({ target: nextRef ?? selfRef, offset: ["start end", `start ${NAV_H + pinTop(i + 1)}px`], layoutEffect: false }); // the next sibling's ref is attached after this layout effect
  const scale = useTransform(cover, [0, 1], [1, nextRef ? 0.92 : 1]);
  const dim = useTransform(cover, [0, 1], [0, nextRef ? 0.55 : 0]);
  useMotionValueEvent(enter, "change", (v) => onEnter(i, v));

  const c = variant === "c";
  return (
    <article
      ref={selfRef}
      id={id}
      aria-labelledby={`${id}-t`}
      className={cn(
        "flex w-full scroll-mt-[calc(var(--nav-h)+12px)] items-start justify-center motion-safe:sticky motion-safe:h-[100svh] motion-safe:min-h-[560px]",
      )}
      style={{ top: `calc(var(--nav-h) + env(safe-area-inset-top, 0px) + ${pinTop(i)}px)` }}
    >
      <motion.div
        style={{ scale, transformOrigin: "50% 0", ...(c ? C_TONES[i] : null) }}
        className={cn(
          "relative isolate flex aspect-[9/16] flex-col overflow-hidden rounded-lg px-[22px] pb-[26px] pt-3.5 [container-type:inline-size] motion-reduce:![transform:none]",
          "w-[min(100%,calc((100svh_-_var(--nav-h)_-_82px)*0.5625),400px)]",
          c
            ? "text-ink shadow-card ring-1 ring-inset ring-line"
            : "border border-line-2 bg-[linear-gradient(170deg,var(--c-raised)_0%,var(--c-surface)_52%,var(--c-bg-2)_100%)] text-ink shadow-card",
        )}
      >
        <Bars i={i} enter={enter} variant={variant} />
        <div className="relative mt-3.5 min-h-0 flex-1" aria-hidden>
          <Art k={k} variant={variant} enter={enter} />
        </div>
        <div className="relative z-[2]">
          <h3 id={`${id}-t`} className="mb-2.5 text-balance font-display text-[length:min(11.5cqi,44px)] font-black leading-[1.32]">
            {k === "backgrounds" ? (
              <span className={c ? "rounded-md bg-accent px-[.22em] leading-[1.45] text-on-accent [box-decoration-break:clone]" : "text-accent-text"}>
                {title}
              </span>
            ) : (
              title
            )}
          </h3>
          <p className={cn("max-w-[30ch] text-[length:max(15px,4.2cqi)] leading-[1.85]", c ? "text-ink" : "text-ink-2")}>{body}</p>
        </div>
        <motion.span aria-hidden style={{ opacity: dim }} className="pointer-events-none absolute inset-0 z-[5] bg-bg motion-reduce:hidden" />
      </motion.div>
    </article>
  );
}

// Story progress bars: earlier stories full, this one fills as the card slides into place.
function Bars({ i, enter, variant }: { i: number; enter: MotionValue<number>; variant: Variant }) {
  return (
    <div className="relative z-[2] flex gap-1" aria-hidden>
      {KEYS.map((k, j) => (
        <span key={k} className="h-[3px] flex-1 overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--c-ink)_30%,transparent)]">
          {j <= i && (
            <motion.span
              className={cn("block h-full origin-right motion-reduce:![transform:none]", variant === "c" ? "bg-ink" : "bg-accent-text")}
              style={j === i ? { scaleX: enter } : undefined}
            />
          )}
        </span>
      ))}
    </div>
  );
}

// Rises into place (scroll-linked) as its card slides in; depth staggers the layers. Static with reduced motion.
function Float({ enter, depth = 1, className, children }: { enter: MotionValue<number>; depth?: number; className?: string; children: ReactNode }) {
  const y = useTransform(enter, [0, 1], [depth * 44, 0]);
  return (
    <motion.span style={{ y }} className={cn("block motion-reduce:![transform:none]", className)}>
      {children}
    </motion.span>
  );
}

function Print({ src, className }: { src: string; className?: string }) {
  return (
    <img
      src={src}
      alt=""
      width={202}
      height={266}
      loading="lazy"
      decoding="async"
      // 5:3 from the top crops away the caption baked into the bottom of the slide
      className={cn("block aspect-[5/3] h-auto w-full rounded-md object-cover object-top shadow-[0_16px_30px_-10px_rgb(5_9_45/0.6)]", className)}
    />
  );
}

function Art({ k, variant, enter }: { k: Key; variant: Variant; enter: MotionValue<number> }) {
  const c = variant === "c";
  const frame = c ? "border-[3px] border-ink" : "border border-line-2";
  const chip = "bg-accent text-on-accent"; // light pill in c, brand blue in a
  const soft = c ? "text-ink" : "text-accent-text";

  if (k === "fonts")
    return (
      <div className="flex h-full flex-col items-start justify-center">
        <Float enter={enter} depth={1.4} className="absolute end-0 top-[2%] w-[44%] max-w-[180px]">
          <Print src="/img/slide_2.png" className={cn("rotate-[7deg]", frame)} />
        </Float>
        <Float enter={enter} depth={0.6} className="relative">
          <span className="block -rotate-3 whitespace-nowrap font-display text-[length:19cqi] font-black leading-[1.22]">سلام</span>
        </Float>
        <Float enter={enter} depth={1} className="relative self-center">
          <span className={cn("block rotate-2 whitespace-nowrap font-alt text-[length:22cqi] leading-[1.15] [font-variation-settings:'wght'_200,'wdth'_70]", soft)}>
            سلام
          </span>
        </Float>
        <Float enter={enter} depth={1.3} className="relative self-end">
          <span className="block whitespace-nowrap font-body text-[length:17cqi] font-thin leading-[1.3]">سلام</span>
        </Float>
        <Float enter={enter} depth={1.8} className="relative mt-1.5">
          <span
            className={cn(
              "block -rotate-2 whitespace-nowrap rounded-md px-[.3em] font-alt text-[length:15cqi] leading-[1.3] [font-variation-settings:'wght'_900,'wdth'_100]",
              chip,
            )}
          >
            سلام
          </span>
        </Float>
      </div>
    );

  if (k === "backgrounds")
    return (
      <>
        <Float enter={enter} depth={0.8} className="absolute start-0 top-[4%] w-[58%] max-w-[210px]">
          <Print src="/img/slide_1.png" className={cn("-rotate-6", frame)} />
        </Float>
        <Float enter={enter} depth={1.6} className="absolute end-0 top-[44%] w-[54%] max-w-[200px]">
          <Print src="/img/slide_3.png" className={cn("rotate-[7deg]", frame)} />
        </Float>
      </>
    );

  if (k === "import")
    return (
      <>
        <span
          className={cn("absolute -top-[16%] end-[-6%] font-alt text-[length:112cqi] leading-none [font-variation-settings:'wght'_900,'wdth'_100]", !c && "text-raised")}
          style={c ? { color: B2 } : undefined}
        >
          ف
        </span>
        <Float enter={enter} depth={1} className="absolute start-0 top-[6%]">
          <span className={cn("ltr-iso block rotate-[4deg] rounded-md px-[.32em] font-display text-[length:13cqi] font-black leading-[1.35]", chip)}>.ttf</span>
        </Float>
        <Float enter={enter} depth={1.5} className="absolute start-[6%] top-[27%]">
          <span className={cn("ltr-iso block -rotate-3 font-body text-[length:12cqi] font-extralight leading-[1.3]", soft)}>.otf</span>
        </Float>
        <Float enter={enter} depth={1.3} className="absolute bottom-[6%] start-0 w-[56%] max-w-[205px]">
          <Print src="/img/slide_5.png" className={cn("-rotate-[5deg]", frame)} />
        </Float>
      </>
    );

  return <Ads enter={enter} frame={frame} c={c} />;
}

function Ads({ enter, frame, c }: { enter: MotionValue<number>; frame: string; c: boolean }) {
  // the strike draws across the word as the card arrives: the ads are what Pro removes
  const strike = useTransform(enter, [0.55, 1], [0, 1]);
  return (
    <>
      <Float enter={enter} depth={1.2} className="absolute end-[2%] top-[4%] w-[52%] max-w-[190px]">
        <Print src="/img/slide_4.png" className={cn("rotate-6", frame)} />
      </Float>
      <span className="absolute bottom-[10%] start-0 font-display text-[length:26cqi] font-black leading-[1.25]">
        تبلیغ
        <span className="absolute inset-x-[-6%] top-1/2 -mt-[.07em] block -rotate-[5deg]">
          <motion.span
            style={{ scaleX: strike }}
            className={cn("block h-[.14em] origin-right rounded-full motion-reduce:![transform:none]", c ? "bg-bg" : "bg-accent-text")}
          />
        </span>
      </span>
    </>
  );
}
